import { useEffect } from 'react';
import { useLang } from '../i18n';

function setMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function setLink(rel, href, hreflang) {
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Un solo H1 per pagina, title/description/hreflang/canonical + JSON-LD.
 * `suffix` (default: " · VdE Tivoli 1996") segue le regole del report copy
 * 19/10/2026 (§6.1): brand pages col suffisso lungo, collezioni/serie con
 * " | VdE" gia' incluso nel seo_title (suffix=""), prodotto con pattern
 * specifico.
 */
export default function Seo({ title, description, image, jsonLd, path, noIndex, suffix }) {
  const { lang } = useLang();
  useEffect(() => {
    const full = title ? `${title}${suffix ?? ' · VdE Tivoli 1996'}` : 'VdE Tivoli 1996 · Vivi di emozioni';
    document.title = full;
    if (noIndex) {
      setMeta('name', 'robots', 'noindex,follow');
    } else {
      // Ripristina il valore di base definito in public/index.html: non va
      // rimosso, altrimenti ogni pagina senza noIndex perde il tag globale.
      setMeta('name', 'robots', 'index,follow');
    }
    if (description) {
      setMeta('name', 'description', description);
      setMeta('property', 'og:description', description);
    }
    setMeta('property', 'og:title', full);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', 'VdE Tivoli 1996');
    setMeta('property', 'og:locale', lang === 'en' ? 'en_GB' : 'it_IT');
    setMeta('name', 'twitter:card', 'summary_large_image');
    const SITE = window.location.origin;
    if (image) {
      const absImage = image.startsWith('/') ? `${SITE}${image}` : image;
      setMeta('property', 'og:image', absImage);
      setMeta('name', 'twitter:image', absImage);
    }
    const p = path || window.location.pathname;
    setLink('canonical', `${SITE}${p}`);
    setLink('alternate', `${SITE}${p}`, 'it');
    setLink('alternate', `${SITE}${p}?lang=en`, 'en');
    setLink('alternate', `${SITE}${p}`, 'x-default');

    const id = 'vde-jsonld';
    document.getElementById(id)?.remove();
    if (jsonLd) {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.id = id;
      // schema.org vuole URL assoluti: le pagine scrivono percorsi relativi,
      // li assolutizziamo qui invece che in ogni chiamante
      const ABS_KEYS = ['item', 'url', 'logo', 'image', 'thumbnailUrl', 'contentUrl'];
      s.textContent = JSON.stringify(jsonLd, (key, value) =>
        ABS_KEYS.includes(key) && typeof value === 'string' && value.startsWith('/')
          ? `${SITE}${value}`
          : value,
      );
      document.head.appendChild(s);
    }
  }, [title, description, image, jsonLd, path, lang, noIndex, suffix]);

  return null;
}
